/**
 * Resolves the Feathers API URL for the current runtime.
 *
 * Keeps NEXT_PUBLIC_API_URL as-is for localhost and remote APIs.
 * When the page is opened via a LAN IP (phone on same Wi‑Fi) and the
 * configured API points at localhost, rewrites the hostname to match
 * window.location so the phone hits the Mac backend instead of itself.
 */
export function resolveApiUrl(
  configuredUrl: string | undefined = process.env.NEXT_PUBLIC_API_URL
): string {
  const fallback = configuredUrl || 'http://localhost:3030'

  if (typeof window === 'undefined') {
    return fallback
  }

  const pageHost = window.location.hostname
  if (!isLanHostname(pageHost) && !isLoopbackHostname(pageHost)) {
    return fallback
  }

  try {
    const api = new URL(fallback)
    if (!isLoopbackHostname(api.hostname) && !isLanHostname(pageHost)) {
      return fallback
    }
    if (api.hostname !== 'localhost' && api.hostname !== '127.0.0.1') {
      return fallback
    }

    api.hostname = pageHost
    return api.toString().replace(/\/$/, '')
  } catch {
    return fallback
  }
}

function isLoopbackHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1'
}

function isLanHostname(hostname: string): boolean {
  if (hostname.endsWith('.local')) return true

  const match = hostname.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/)
  if (!match) return false

  const octets = match.slice(1).map(Number)
  if (octets.some((n) => n > 255)) return false

  const [a, b] = octets
  if (a === 10) return true
  if (a === 192 && b === 168) return true
  if (a === 172 && b >= 16 && b <= 31) return true
  return false
}
